/**
 * Messages between the main thread and the face tracking worker.
 * All landmark data travels as transferable Float32Arrays (zero-copy).
 */
export type Delegate = 'GPU' | 'CPU';

export interface TrackerInitOptions {
  /** Absolute URL of the folder holding the MediaPipe wasm files (ignored when the explicit paths below are set). */
  wasmBasePath: string;
  /**
   * Explicit locations of the wasm loader (.js) and binary (.wasm). Use these
   * when the files cannot live in one folder (flat CDNs such as Shopify Files).
   */
  wasmLoaderPath?: string;
  wasmBinaryPath?: string;
  /** Absolute URL of face_landmarker.task. */
  modelAssetPath: string;
  delegate: Delegate;
  /**
   * Load the ES-module flavour of the wasm loader (`vision_wasm_module_internal.js`).
   * Required inside a *module* worker, where the classic loader cannot set its
   * global `ModuleFactory` ("ModuleFactory not set"). False on the main thread.
   */
  useModule: boolean;
  /** 1 = fastest (detector skipped while a face is tracked). See docs/00 for the trade-off. */
  numFaces: number;
  minFaceDetectionConfidence: number;
  minFacePresenceConfidence: number;
  minTrackingConfidence: number;
}

export const DEFAULT_TRACKER_OPTIONS: Omit<TrackerInitOptions, 'wasmBasePath' | 'modelAssetPath'> = {
  delegate: 'GPU',
  useModule: false,
  numFaces: 1,
  minFaceDetectionConfidence: 0.5,
  minFacePresenceConfidence: 0.5,
  minTrackingConfidence: 0.5,
};

export interface FrameMessage {
  type: 'frame';
  bitmap: ImageBitmap;
  /** Monotonic timestamp (ms) handed to MediaPipe — must strictly increase. */
  timestampMs: number;
  /** performance.now() when the frame was grabbed (latency measurement). */
  captureTs: number;
  seq: number;
}

export type ToWorkerMessage =
  | { type: 'init'; options: TrackerInitOptions }
  | FrameMessage
  | { type: 'setNumFaces'; numFaces: number }
  | { type: 'close' };

export interface TrackerResult {
  type: 'result';
  seq: number;
  timestampMs: number;
  captureTs: number;
  /** Inference wall time in the worker (ms). */
  inferMs: number;
  /** Null when no face is detected. */
  landmarks: Float32Array | null;
  matrix: Float32Array | null;
  /** Number of faces seen by the landmarker (largest one is returned). */
  faceCount: number;
  /** Dimensions of the bitmap that was processed. */
  width: number;
  height: number;
}

export type FromWorkerMessage =
  | { type: 'ready'; delegate: Delegate; initMs: number }
  | TrackerResult
  | { type: 'error'; message: string; fatal: boolean }
  /** A110Y: the landmarker is closed (GPU textures and wasm graph released); the worker may be terminated. */
  | { type: 'closed' };
